import { observer } from 'mobx-react-lite';
import { GridCardItem } from './GridCardItem';
import React, { ReactElement } from 'react';
import { ActionsCellAPI, GridState } from '../../state';
import { FiltersMap } from '@wix/bex-core';
import { AddItemProps } from '@wix/design-system';
import { DragAndDrop } from '../DragAndDrop';
import { ActionsCellProps } from '../ActionsCell';
import { SupportedCardGalleryItemProps } from './SupportedCardGalleryItemProps';

export interface GridItemProps<T, F extends FiltersMap> {
  state: GridState<T, F>;
  preset?: 'full' | 'title' | 'empty';
  renderAddItem?: () => ReactElement<AddItemProps>;
  renderItem?: (
    item: T,
    index: number,
  ) => Partial<SupportedCardGalleryItemProps>;

  index: number;
  rowIndex?: number;
  columnIndex?: number;
  actionsCell?:
    | ActionsCellProps
    | ((item: T, index: number, api: ActionsCellAPI<T, F>) => ActionsCellProps);
  dragAndDrop?: DragAndDrop;
  isOverlay?: boolean;
}

function _GridItem<T, F extends FiltersMap>(props: GridItemProps<T, F>) {
  const {
    state,
    preset,
    index,
    rowIndex,
    columnIndex,
    renderItem,
    renderAddItem,
    actionsCell,
    dragAndDrop,
    isOverlay,
  } = props;
  const renderType = state.getItemRenderTypeAt(index);

  const {
    collection: {
      query: { hasNonPersistentActiveFilters },
    },
  } = state;

  if (renderType === null) {
    return null;
  }

  if (renderType === 'trailing') {
    if (hasNonPersistentActiveFilters) {
      return null;
    }

    return (
      <div
        key="add-item"
        data-hook="grid-add-item-container"
        style={{ height: '100%' }}
        onClick={() => state.onAddItemClick()}
      >
        {renderAddItem?.()}
      </div>
    );
  }

  let child = (
    <GridCardItem
      state={state}
      index={index}
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      keyedItem={renderType}
      renderItem={renderItem}
      actionsCell={actionsCell}
      preset={preset}
    />
  );

  if (dragAndDrop) {
    const { DraggableCard } = dragAndDrop;
    child = (
      <DraggableCard
        id={renderType.key}
        index={renderType.index}
        state={state}
        isOverlay={isOverlay}
      >
        {child}
      </DraggableCard>
    );
  }

  return child;
}

export const GridItem = observer(_GridItem);
